import { useField, useFormikContext } from "formik";
import { useEffect, useMemo, useRef } from "react";
import type {
  ActionProps,
  ActionWithRulesAndAddersProps,
  ActionWithRulesProps,
  RuleGroupTypeIC
} from "react-querybuilder";
import { QueryBuilder, convertQuery, formatQuery } from "react-querybuilder";
import { defaultOperators } from "react-querybuilder";
import type { Field, RuleType } from "react-querybuilder";

import transform from "lodash/transform";
import isObject from "lodash/isObject";
import invert from "lodash/invert";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";

import "./query.scss";
import ErrorField from "shared/form/fields/errorField";
import { Copies, useCopies } from "shared/hooks/useCopies";
import Icon from "assets/icons";
import { removeHtmlTagsFromString } from "shared/utils";
import { ApiSelectionFeaturedListConfig } from "shared/types/selection/ApiSelectionFeaturedListConfig";
import { FieldValidator } from "formik/dist/types";

const initialQuery: RuleGroupTypeIC = { rules: [] };
const mapper: { [key: string]: string } = {
  combinator: "conjugationType",
  rules: "criterias",
  field: "key",
  and: "AND",
  or: "OR"
};

export function processCriteriaForQueryBuilder(actorCriteriaFromApi?: object) {
  if (!actorCriteriaFromApi) {
    return initialQuery;
  }
  const withKeysReplaced = replaceKeysDeep(actorCriteriaFromApi, invert(mapper));
  const convertedQuery = convertQuery(withKeysReplaced);
  const hasNestedRules =
    (convertedQuery as RuleGroupTypeIC).rules.filter(item => {
      if (typeof item === "object") {
        return "rules" in item;
      }
      return false;
    }).length > 0;

  if (hasNestedRules) {
    return convertedQuery;
  }

  return { ...convertedQuery, rules: [{ ...convertedQuery }] };
}

export function getTransformedCriteriaForApi(q: RuleGroupTypeIC | null) {
  if (q === null) {
    return null;
  }

  const convertedQuery = convertQuery(q);
  const formattedQuery = JSON.parse(formatQuery(convertedQuery, "json_without_ids"));
  const withKeysReplaced = replaceKeysDeep(formattedQuery, mapper);

  return withKeysReplaced;
}

export function validateQueryFromQueryBuilder(
  q: RuleGroupTypeIC<RuleType<string, string, any, string>, string>,
  copies: Copies
): { isValid: boolean; message: string } {
  const validations: any = {};
  const processGroup = (g: any) => {
    if (g?.rules?.length === 0) {
      validations[g.id] = false;
    }
    g?.rules?.forEach((r: any) => {
      try {
        if ("rules" in r) {
          processGroup(r);
        } else {
          if (!r.value) {
            validations[r.id] = false;
          }
        }
      } catch (error) {}
    });
  };
  processGroup(q);
  return {
    isValid: isEmpty(validations),
    message: q?.rules?.length === 0 ? copies.get("required") : copies.get("fsl.config.all.rules.be.valid")
  };
}

export function validateQueryInValidationSchema(q: any, copies: Copies) {
  return validateQueryFromQueryBuilder(q, copies);
}

function replaceKeysDeep(obj: any, keysMap: any) {
  // keysMap = { oldKey1: newKey1, oldKey2: newKey2, etc...
  return transform(obj, function (result: any, value, key) {
    // transform to a new object
    let currentKey = keysMap[key] || key; // if the key is in keysMap use the replacement, if not use the original key
    let val = value;
    if (key === "conjugationType" || key === "combinator") {
      val = keysMap[value];
    }

    result[currentKey] = isObject(value) ? replaceKeysDeep(value, keysMap) : val; // if the key is an object run it through the inner function - replaceKeys
  });
}

export const validator = (r: RuleType) => !!r.value;

export function AddSearchCriteria({
  searchCriteriaConfigFieldOptions,
  resetFieldWhenRemoved,
  name,
  disabled,
  className,
  hasGroupAction = true,
  validate
}: {
  name: string;
  resetFieldWhenRemoved?: boolean;
  searchCriteriaConfigFieldOptions?: ApiSelectionFeaturedListConfig;
  disabled?: boolean;
  className?: string;
  hasGroupAction?: boolean;
  validate?: FieldValidator;
}) {
  const [{ value }, meta] = useField({
    name,
    validate
  });
  const copies = useCopies();
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const renderRef = useRef(false);

  const queryFieldss: Array<Field> = useMemo(() => {
    const operatorsCopyMap: any = {
      "=": copies.get("fsl.query.builder.operator.equal"),
      "!=": copies.get("fsl.query.builder.operator.not.equal"),
      undefined: copies.get("fsl.query.builder.operator.not.defined")
    };
    if (searchCriteriaConfigFieldOptions?.fields?.length) {
      return sortBy(
        searchCriteriaConfigFieldOptions?.fields
          .filter(field => field && field.options?.length)
          .map(field => ({
            name: field.id,
            label: removeHtmlTagsFromString(field.name || ""),
            valueEditorType: "select",
            defaultValue: "",
            values: [
              { name: "", label: "---" },
              ...sortBy(field.options, "displayOrder").map(option => ({ name: option.id, label: option.name }))
            ],
            valueSources: ["value"],
            operators: defaultOperators
              .filter(op => op.name === "=" || op.name === "!=")
              .map(operator => {
                return {
                  ...operator,
                  label: operatorsCopyMap[operator.name]
                };
              }),
            defaultOperator: "=",
            validator
          })),
        o => o.label?.toLowerCase()
      );
    } else {
      return [];
    }
  }, [searchCriteriaConfigFieldOptions, copies]);

  return (
    <div onClick={() => setFieldTouched(name, true)} className={className}>
      <div className="queryBuilder_wrapper">
        <QueryBuilder
          fields={queryFieldss}
          disabled={!!disabled}
          query={value}
          onQueryChange={q => {
            // currently the query builder triggers a change on mount to add uuids to the query in first render so we need to ignore that
            // we have to either store with uuids in BE or add back the uuids when initialising the query builder
            if (renderRef.current) {
              setFieldTouched(name, true);
              setFieldValue(name, q);
            } else {
              renderRef.current = true;
            }
          }}
          autoSelectOperator={true}
          autoSelectField={true}
          resetOnFieldChange={true}
          resetOnOperatorChange={true}
          independentCombinators
          controlElements={{
            addGroupAction: hasGroupAction ? AddGroupAction : NullGroupAction,
            addRuleAction: AddRuleAction,
            removeRuleAction: RemoveRuleAction,
            removeGroupAction: RemoveGroupAction
          }}
        />
      </div>
      <ErrorField meta={meta} />
    </div>
  );
}

function NullGroupAction() {
  return null;
}
function AddGroupAction(props: ActionWithRulesAndAddersProps) {
  const copies = useCopies();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (props.rules?.length === 0 && props.level === 0) {
      ref.current?.click();
    }
  }, [props.rules, props.level]);

  return (
    <button
      ref={ref}
      style={{ visibility: props.level === 0 ? "visible" : "hidden" }}
      type="button"
      data-testid={props.testID}
      className={props.className}
      onClick={props.handleOnClick}>
      <Icon.AddCircle />
      {copies.get("fsl.config.add.group")}
    </button>
  );
}

function AddRuleAction(props: ActionWithRulesAndAddersProps) {
  const copies = useCopies();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (props.rules?.length === 0 && props.level > 0) {
      ref.current?.click();
    }
  }, [props.rules, props.level]);

  const visibility: "hidden" | "visible" = useMemo(() => {
    if (props.rules?.length === 0 || props.level === 0) {
      return "hidden";
    } else {
      return "visible";
    }
  }, [props.rules, props.level]);

  return (
    <button
      ref={ref}
      type="button"
      data-testid={props.testID}
      style={{
        visibility
      }}
      className={props.className}
      onClick={props.handleOnClick}>
      <Icon.AddCircle />
      {copies.get("fsl.config.add.rule")}
    </button>
  );
}

function RemoveRuleAction(props: ActionProps) {
  return (
    <button
      type="button"
      className={props.className}
      data-testid={props.testID}
      onClick={props.handleOnClick}
      style={{ display: props.path?.[1] === 0 ? "none" : "flex" }}>
      <Icon.RemoveCircle />
    </button>
  );
}

function RemoveGroupAction(props: ActionWithRulesProps) {
  return (
    <button
      type="button"
      className={props.className}
      data-testid={props.testID}
      onClick={props.handleOnClick}
      style={{ display: props.path?.[0] === 0 ? "none" : "flex" }}>
      <Icon.Close />
    </button>
  );
}
